/**
 * KeyDerivation - Deterministic encryption key derivation from wallet
 *
 * Uses EIP-712 typed data signing for domain-separated key derivation.
 * Same wallet always produces the same keys. Different purposes produce
 * different keys via domain separation in the EIP-712 message.
 *
 * Flow: wallet.signTypedData → HKDF(signature) → AES-GCM CryptoKey
 *
 * @see docs/architecture/OwnYou_architecture_v13.md Section 7
 */
/** EIP-712 domain separator for OwnYou key derivation */
const OWNYOU_DOMAIN = {
    name: 'OwnYou',
    version: '1',
};
/** EIP-712 type definition for key derivation messages */
const KEY_DERIVATION_TYPES = {
    KeyDerivation: [
        { name: 'purpose', type: 'string' },
        { name: 'version', type: 'uint256' },
    ],
};
const HKDF_INFO_STRINGS = {
    'store-encryption': 'ownyou:v1:encryption:store',
    'sync-encryption': 'ownyou:v1:encryption:sync',
    'bbs-credential-seed': 'ownyou:v1:credential:bbs-seed',
    'bbs-pseudonym': 'ownyou:v1:pseudonym:nym-secret',
    'bbs-signing': 'ownyou:v1:credential:bbs-signing',
    'hd-payment': 'ownyou:v1:payment:hd-seed',
    'master-key-derivation': 'ownyou:v1:master:derivation',
};
export class KeyDerivation {
    /**
     * Derive both Store and Sync keys from a single EIP-712 signature.
     * Only triggers Privy's signing modal ONCE instead of twice.
     *
     * Signs with 'master-key-derivation' purpose, then derives both keys
     * via HKDF with different info strings for domain separation.
     */
    async deriveAllKeys(signer) {
        const signature = await this.signForPurpose(signer, 'master-key-derivation');
        return this.deriveAllKeysFromSignature(signature, signer.getAddress());
    }
    /**
     * Get the master EIP-712 signature for key derivation.
     * This is the step that triggers Privy's signing modal.
     * Callers can cache the returned signature and later pass it
     * to deriveAllKeysFromSignature() to avoid re-prompting.
     */
    async getMasterSignature(signer) {
        return this.signForPurpose(signer, 'master-key-derivation');
    }
    /**
     * Derive both Store and Sync keys from a pre-obtained master signature.
     * Does NOT trigger the Privy signing modal — use when you have a
     * cached signature from a previous getMasterSignature() call.
     */
    async deriveAllKeysFromSignature(signature, address) {
        const signatureBytes = hexToBytes(signature);
        const [storeKey, syncKey] = await Promise.all([
            this.deriveAESKeyFromSignature(signatureBytes, address, 'store-encryption'),
            this.deriveAESKeyFromSignature(signatureBytes, address, 'sync-encryption'),
        ]);
        return { storeKey, syncKey };
    }
    /**
     * Derive deterministic AES-GCM encryption key for Store data.
     * Uses EIP-712 typed data signing with 'store-encryption' purpose.
     * NOTE: Triggers its own signing modal. Prefer deriveAllKeys() for login flow.
     */
    async deriveStoreKey(signer) {
        return this.deriveAESKey(signer, 'store-encryption');
    }
    /**
     * Derive deterministic AES-GCM encryption key for E2EE sync.
     * Different domain from Store key for separation.
     * NOTE: Triggers its own signing modal. Prefer deriveAllKeys() for login flow.
     */
    async deriveSyncKey(signer) {
        return this.deriveAESKey(signer, 'sync-encryption');
    }
    /**
     * Derive seed bytes for BBS+ credential generation.
     * Returns raw 32 bytes, not a CryptoKey.
     */
    async deriveBBSCredentialSeed(signer) {
        const signature = await this.signForPurpose(signer, 'bbs-credential-seed');
        const signatureBytes = hexToBytes(signature);
        const keyMaterial = await crypto.subtle.importKey('raw', signatureBytes.buffer, { name: 'HKDF' }, false, ['deriveBits']);
        const salt = await this.generateSalt(signer.getAddress(), 'bbs-credential-seed');
        const info = new TextEncoder().encode('ownyou:v1:credential:bbs-seed');
        const seedBuffer = await crypto.subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: salt.buffer, info: info.buffer }, keyMaterial, 256);
        return new Uint8Array(seedBuffer);
    }
    /**
     * Derive nymSecret for pseudonym/tracking_ID generation.
     * nymSecret = HKDF(walletSeed, "ownyou:bbs:pseudonym")
     * Returns raw 32 bytes (used as BLS12-381 scalar).
     *
     * @traces R4.4
     */
    async deriveNymSecret(signer) {
        return this.deriveRawBytes(signer, 'bbs-pseudonym', 'ownyou:v1:pseudonym:nym-secret');
    }
    /**
     * Derive BBS+ signing key pair for credential issuance.
     * signingKey = HKDF(walletSeed, "ownyou:bbs:signing")
     * Returns { secretKey, publicKey } — publicKey is derived from secretKey
     * using the BLS12-381 G2 generator multiplication.
     *
     * Note: The publicKey derivation uses a simplified approach (SHA-256 hash
     * of secretKey) as a placeholder until @digitalbazaar/bbs-signatures is
     * wired in Package 2. The secretKey is the real cryptographic material.
     *
     * @traces Architecture §2.1
     */
    async deriveBBSSigningKeyPair(signer) {
        const secretKey = await this.deriveRawBytes(signer, 'bbs-signing', 'ownyou:v1:credential:bbs-signing');
        // Derive public key from secret key.
        // In production this will be G2 * secretKey via @noble/curves.
        // For now, derive a deterministic 48-byte public key via SHA-256 expansion.
        const pkHash1 = await crypto.subtle.digest('SHA-256', new Uint8Array([...secretKey, ...new TextEncoder().encode('ownyou:v1:credential:bbs-pk:0')]));
        const pkHash2 = await crypto.subtle.digest('SHA-256', new Uint8Array([...secretKey, ...new TextEncoder().encode('ownyou:v1:credential:bbs-pk:1')]));
        const publicKey = new Uint8Array(48);
        publicKey.set(new Uint8Array(pkHash1).slice(0, 32), 0);
        publicKey.set(new Uint8Array(pkHash2).slice(0, 16), 32);
        return { secretKey, publicKey };
    }
    /**
     * Derive HD wallet seed for per-campaign payment addresses.
     * hdSeed = HKDF(walletSeed, "ownyou:hd:payment")
     * Returns raw 32 bytes (used as BIP-32 master seed).
     *
     * @traces R7.2
     */
    async deriveHDWalletSeed(signer) {
        return this.deriveRawBytes(signer, 'hd-payment', 'ownyou:v1:payment:hd-seed');
    }
    /** Derive raw 32 bytes via HKDF with a specific purpose and info string */
    async deriveRawBytes(signer, purpose, info) {
        const signature = await this.signForPurpose(signer, purpose);
        const signatureBytes = hexToBytes(signature);
        const keyMaterial = await crypto.subtle.importKey('raw', signatureBytes.buffer, { name: 'HKDF' }, false, ['deriveBits']);
        const salt = await this.generateSalt(signer.getAddress(), purpose);
        const infoBytes = new TextEncoder().encode(info);
        const seedBuffer = await crypto.subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: salt.buffer, info: infoBytes.buffer }, keyMaterial, 256);
        return new Uint8Array(seedBuffer);
    }
    /** Core: derive an AES-GCM-256 key from wallet signature via HKDF */
    async deriveAESKey(signer, purpose) {
        const signature = await this.signForPurpose(signer, purpose);
        const signatureBytes = hexToBytes(signature);
        return this.deriveAESKeyFromSignature(signatureBytes, signer.getAddress(), purpose);
    }
    /** Derive AES-GCM-256 key from pre-obtained signature bytes + HKDF */
    async deriveAESKeyFromSignature(signatureBytes, address, purpose) {
        const keyMaterial = await crypto.subtle.importKey('raw', signatureBytes.buffer, { name: 'HKDF' }, false, ['deriveKey']);
        const salt = await this.generateSalt(address, purpose);
        const infoStr = HKDF_INFO_STRINGS[purpose];
        if (!infoStr) {
            throw new Error(`No HKDF info string defined for purpose: ${purpose}`);
        }
        const info = new TextEncoder().encode(infoStr);
        return crypto.subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: salt.buffer, info: info.buffer }, keyMaterial, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    }
    /** Sign EIP-712 typed data for a specific key purpose */
    async signForPurpose(signer, purpose) {
        return signer.signTypedData({
            domain: OWNYOU_DOMAIN,
            types: KEY_DERIVATION_TYPES,
            primaryType: 'KeyDerivation',
            message: { purpose, version: 1 },
        });
    }
    /** Generate deterministic salt from address + purpose (SHA-256 hash) */
    async generateSalt(address, purpose) {
        const saltString = `${address.toLowerCase()}-${purpose}-v1`;
        const saltBytes = new TextEncoder().encode(saltString);
        const hashBuffer = await crypto.subtle.digest('SHA-256', saltBytes);
        return new Uint8Array(hashBuffer);
    }
}
/** Convert 0x-prefixed hex string to bytes */
function hexToBytes(hex) {
    const hexString = hex.slice(2);
    const bytes = new Uint8Array(hexString.length / 2);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hexString.slice(i * 2, i * 2 + 2), 16);
    }
    return bytes;
}
